import { useParams, Navigate, Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import {
  Building2,
  Target,
  Globe,
  Lightbulb,
  ArrowRight,
  CheckCircle2,
  RefreshCw,
  ArrowLeft,
  Layers,
  Activity,
  Sparkles,
} from 'lucide-react';
import { companiesApi, Company } from '../services/api';
import { loadCompanyProgress, type CompanyProgressResult } from '../utils/companyProgress';
import { getFoundationProgress, getPlanningProgress } from '../utils/dashboard';
import Header from '../components/Header';
import './strategy/StrategyWorkspace.css';
import '../ai-plans/Plans.css';

const planningStages = [
  {
    id: 'foundation',
    title: 'Foundation',
    icon: Target,
    description: 'Give your company a shared direction and define what success looks like.',
    components: ['Vision', 'Mission', 'Values', 'Objectives'],
    sections: ['vision', 'mission', 'values', 'objectives'],
  },
  {
    id: 'external-assessment',
    title: 'External assessment',
    icon: Globe,
    description: 'Understand your market, competitors, and the forces shaping your opportunity.',
    components: ['PESTEL', 'Five Forces', 'Competitors', 'Driving forces'],
    sections: ['pestel', 'fiveForces', 'competitors', 'drivingForces'],
  },
  {
    id: 'internal-assessment',
    title: 'Internal assessment',
    icon: Building2,
    description: 'Find the strengths you can build on and the capabilities you need to develop.',
    components: ['SWOT analysis', 'Success factors'],
    sections: ['swot', 'industrySuccessFactors'],
  },
  {
    id: 'formulation',
    title: 'Strategy formulation',
    icon: Lightbulb,
    description:
      'Turn your analysis into focused priorities and strategies linked to your objectives.',
    components: ['Plan summary', 'Strategies'],
    sections: ['summary', 'strategies'],
  },
];

const sectionHref = (companyId: string, stage: string, section: string) =>
  stage === 'foundation' || stage === 'formulation'
    ? `/company/${companyId}/strategy/${stage}/${section}`
    : `/company/${companyId}/strategy/${stage}#${section}`;

export default function CompanyStrategyPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const [company, setCompany] = useState<Company | null>(null);
  const [companyProgress, setCompanyProgress] = useState<CompanyProgressResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setCompanyProgress(null);

    const load = async () => {
      const [companyResult, progressResult] = await Promise.allSettled([
        companiesApi.get(companyId),
        loadCompanyProgress(companyId),
      ]);
      if (cancelled) return;
      if (
        companyResult.status === 'fulfilled' &&
        companyResult.value.success &&
        companyResult.value.data
      ) {
        setCompany(companyResult.value.data);
      } else {
        setError('We couldn’t load this company’s plan. Please try again.');
      }
      if (progressResult.status === 'fulfilled') {
        setCompanyProgress(progressResult.value);
      }
      setIsLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const refresh = useCallback(async () => {
    if (!companyId || isRefreshing) return;
    setIsRefreshing(true);
    try {
      setCompanyProgress(await loadCompanyProgress(companyId));
    } finally {
      setIsRefreshing(false);
    }
  }, [companyId, isRefreshing]);

  if (!companyId) return <Navigate to="/dashboard" replace />;
  if (isLoading) {
    return (
      <div className="strategy-plan-loading" role="status">
        <span />
        Loading your strategic plan…
      </div>
    );
  }
  if (error || !company) {
    return (
      <div className="strategy-plan-loading">
        <Building2 size={36} />
        <h1>{error || 'Company not found'}</h1>
        <Link to="/dashboard">Back to all companies</Link>
      </div>
    );
  }

  const foundationData = companyProgress?.foundation ?? null;
  const foundation = foundationData ? getFoundationProgress(foundationData) : null;
  const external = companyProgress?.externalSections ?? null;
  const internal = companyProgress?.internalSections ?? null;
  const formulation = companyProgress?.formulationSections ?? null;
  const average = (values: number[]) =>
    Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
  const stageProgress = [
    foundation,
    companyProgress?.progress?.externalAssessmentProgress ??
      (external ? average(Object.values(external)) : null),
    companyProgress?.progress?.internalAssessmentProgress ??
      (internal ? average(Object.values(internal)) : null),
    companyProgress?.progress?.formulationProgress ??
      (formulation ? average(Object.values(formulation)) : null),
  ];
  const progressAvailable = Boolean(companyProgress?.progress);
  const overall = companyProgress?.progress ? getPlanningProgress(companyProgress.progress) : null;
  const completed = stageProgress.filter((progress) => progress === 100).length;
  const nextIndex = stageProgress.findIndex((progress) => progress === null || progress < 100);
  const nextStage = nextIndex >= 0 ? planningStages[nextIndex] : null;
  const nextHref = nextStage
    ? sectionHref(companyId, nextStage.id, nextStage.sections[0])
    : `/company/${companyId}/execution`;
  const componentsComplete = [
    ['vision', 'mission', 'values', 'objectives'].map((key) =>
      foundationData ? getFoundationProgress({ [key]: foundationData[key] }) === 25 : false
    ),
    external
      ? [external.pestel, external.fiveForces, external.competitors, external.drivingForces].map(
          (value) => value >= 95
        )
      : [false, false, false, false],
    internal
      ? [internal.swot, internal.industrySuccessFactors].map((value) => value >= 95)
      : [false, false],
    formulation
      ? [formulation.summary, formulation.strategies].map((value) => value >= 80)
      : [false, false],
  ];

  return (
    <div className="strategy-plan-page">
      <Header
        title="Build a plan your team can put to work."
        subtitle="One connected journey from your company's direction to the work that moves it forward."
        company={{ name: company.name, logo: company.logo }}
        actions={
          <Link className="strategy-plan-outline" to={`/company/${companyId}`}>
            <ArrowLeft size={15} />
            Company overview
          </Link>
        }
      />
      <main className="strategy-plan-main">
        <section className="ai-plan-entry" aria-label="AI strategic planning">
          <div>
            <strong>Build a full plan from your company brief</strong>
            <p>
              AI develops the assessments, strategy and delivery work, then prepares the report and
              presentation.
            </p>
          </div>
          <Link to={`/company/${companyId}/ai-plan`}>
            Create with AI <Sparkles size={16} />
          </Link>
        </section>
        <section className="strategy-plan-next">
          <div className="strategy-plan-next-copy">
            <p className="strategy-workspace-eyebrow">Your next step</p>
            <h2>
              {progressAvailable
                ? nextStage
                  ? `Keep moving with ${nextStage.title.toLowerCase()}.`
                  : 'Your plan is ready for action.'
                : 'Keep building your strategic plan.'}
            </h2>
            <p>
              {progressAvailable && nextStage
                ? nextStage.description
                : progressAvailable
                  ? 'Connect your priorities to projects, owners, and practical next steps in your execution workspace.'
                  : 'Open any stage below to review your work and continue where you left off.'}
            </p>
            <Link className="strategy-workspace-next" to={nextHref}>
              {progressAvailable && !nextStage ? 'Open execution' : 'Continue planning'}
              <ArrowRight size={16} />
            </Link>
          </div>
          <div className="strategy-plan-summary">
            <div>
              <span>Planning progress</span>
              <strong>{progressAvailable ? `${overall}%` : '—'}</strong>
            </div>
            <div
              role="progressbar"
              aria-label="Planning progress"
              aria-valuenow={overall ?? undefined}
              aria-valuemin={0}
              aria-valuemax={100}
              className="strategy-workspace-progress-track"
            >
              <span style={{ width: `${progressAvailable ? overall : 0}%` }} />
            </div>
            <p>
              {progressAvailable
                ? `${completed} of 4 planning stages complete`
                : 'Some progress is unavailable'}
            </p>
            <button
              type="button"
              onClick={refresh}
              disabled={isRefreshing}
              className="strategy-plan-refresh"
            >
              <RefreshCw size={13} className={isRefreshing ? 'animate-spin' : ''} />
              {isRefreshing ? 'Refreshing…' : 'Refresh progress'}
            </button>
          </div>
        </section>
        <div className="strategy-plan-section-heading">
          <div>
            <p className="strategy-workspace-eyebrow">Your planning journey</p>
            <h2>Four stages. One clear direction.</h2>
          </div>
          <span>
            <Layers size={15} />
            12 connected frameworks and sections
          </span>
        </div>
        <div className="strategy-plan-stages">
          {planningStages.map((stage, index) => {
            const Icon = stage.icon;
            const progress = stageProgress[index];
            const available = progress !== null;
            return (
              <section key={stage.id} className="strategy-plan-stage">
                <div className="strategy-plan-stage-top">
                  <span className={`strategy-plan-stage-icon tone-${index}`}>
                    <Icon size={21} />
                  </span>
                  <span className="strategy-plan-step">Step {index + 1}</span>
                </div>
                <h3>{stage.title}</h3>
                <p>{stage.description}</p>
                <div className="strategy-plan-stage-progress">
                  <span>
                    {available
                      ? progress === 100
                        ? 'Complete'
                        : progress !== null && progress > 0
                          ? 'In progress'
                          : 'Not started'
                      : 'Progress unavailable'}
                  </span>
                  <strong>{available ? `${progress}%` : '—'}</strong>
                </div>
                <div className="strategy-workspace-progress-track">
                  <span style={{ width: `${available ? progress : 0}%` }} />
                </div>
                <div className="strategy-plan-components">
                  {stage.components.map((component, sectionIndex) => (
                    <Link
                      key={component}
                      to={sectionHref(companyId, stage.id, stage.sections[sectionIndex])}
                    >
                      {componentsComplete[index][sectionIndex] ? (
                        <CheckCircle2 size={14} className="is-complete" />
                      ) : (
                        <span className="strategy-plan-component-dot" />
                      )}
                      <span>{component}</span>
                      <ArrowRight size={13} />
                    </Link>
                  ))}
                </div>
                <Link
                  className="strategy-plan-stage-action"
                  to={sectionHref(companyId, stage.id, stage.sections[0])}
                >
                  {available && progress === 100
                    ? 'Review stage'
                    : progress !== null && progress > 0
                      ? 'Continue stage'
                      : 'Start stage'}
                  <ArrowRight size={15} />
                </Link>
              </section>
            );
          })}
        </div>
        <section className="strategy-plan-execution">
          <div className="strategy-plan-execution-icon">
            <Activity size={24} />
          </div>
          <div>
            <p className="strategy-workspace-eyebrow">Step 5 · Execution</p>
            <h2>Turn your priorities into visible progress.</h2>
            <p>Connect projects, tasks, owners, and timelines to the strategy you have built.</p>
          </div>
          <Link className="strategy-plan-outline" to={`/company/${companyId}/execution`}>
            Open execution
            <ArrowRight size={16} />
          </Link>
        </section>
      </main>
    </div>
  );
}
